
import React from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Form } from '@/components/ui/form';
import SymptomCheckboxField from './fields/SymptomCheckboxField';
import FormActions from './fields/FormActions';
import { supabase } from "@/integrations/supabase/client";
import { useUser } from '@/context/UserContext';
import { toast } from 'sonner';

const symptomsSchema = z.object({
  selectedSymptoms: z.array(z.string()).min(1, {
    message: "Please select at least one symptom"
  })
});

interface SymptomsEditProps {
  data: z.infer<typeof symptomsSchema>;
  onSave: (data: z.infer<typeof symptomsSchema>) => void;
  onCancel: () => void;
}

const SymptomsEdit: React.FC<SymptomsEditProps> = ({ data, onSave, onCancel }) => {
  const { user } = useUser();
  const form = useForm<z.infer<typeof symptomsSchema>>({
    resolver: zodResolver(symptomsSchema),
    defaultValues: data
  });

  const handleSubmit = async (values: z.infer<typeof symptomsSchema>) => {
    try {
      // Save to Supabase symptom_logs table if user is logged in
      if (user?.id) {
        const standardSymptoms = ['Fatigue', 'Headache', 'Bloating', 'Cramps', 
                              'Mood Swings', 'Insomnia', 'Low Energy', 'Cravings'];
        
        const customSymptoms = values.selectedSymptoms.filter(
          symptom => symptom !== 'none' && !standardSymptoms.includes(symptom)
        );
        
        await supabase.from('symptom_logs').insert({
          user_id: user.id,
          symptoms: values.selectedSymptoms,
          custom_symptoms: customSymptoms,
          logged_at: new Date().toISOString(),
          notes: 'Updated from profile page'
        });
      }
      
      // Call the original onSave function
      onSave(values);
    } catch (error) {
      console.error('Error saving symptoms:', error);
      toast.error('Failed to save symptoms. Please try again.');
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <div className="text-sm text-muted-foreground mb-4">
          Select the symptoms you're currently experiencing:
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <SymptomCheckboxField form={form} />
        </div>

        <FormActions onCancel={onCancel} />
      </form>
    </Form>
  );
};

export default SymptomsEdit;
